/* eslint-disable max-lines */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  Optional,
  Output,
  Renderer2,
  Self,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { NgControl } from '@angular/forms';

import { fromEvent as observableFromEvent } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { I18nService } from '@recruitee/i18n';
import { generateUuid, includesWithTrim, WithDefaultValue } from '@recruitee/utils';

import { WindowRef } from '../../common/window-ref';
import { KeyCode } from '../../core/keycode';
import { ConnectedOverlayDirective } from '../../core/overlay/overlay-directives';
import { ConnectionPositionPair } from '../../core/overlay/position/connected-position';
import { IconName } from '../../icons-container/icons.type';
import { RtInput } from '../../utils/rt-input';

export type SelectOptionValue = string | number | null;
export type SelectOption<T = SelectOptionValue> = {
  label: string;
  value: T | null;
  disabled?: boolean;
  count?: number;
  additionalSearchKeys?: string[];
};

export type SelectGroupedOptions<T = SelectOptionValue> = {
  title: string | null;
  options: SelectGroupOption<T>[];
};

export interface SelectGroupOption<T = SelectOptionValue> extends SelectOption<T> {
  uuid?: string;
}

export interface TriggerTemplateContext<O = SelectOption, I = any> {
  disabled: boolean;
  readOnly: boolean;
  placeholder: string;
  selectedOption: O;
  dropdownOpened: boolean;
  toggleDropdown: (open?: boolean) => void;
  $implicit: I;
}

export interface LabelTemplateContext<O = SelectOption> {
  option: O;
  removeValue: (optionValue: SelectOptionValue) => void;
  wrapLabelText: boolean;
}

export interface GroupsTemplateContext {
  $implicit: SelectGroupedOptions[];
}

export interface SelectListTemplateContext {
  $implicit: SelectOption[];
}

export interface SelectOptionContext<T> {
  option: SelectOption<T>;
}

export interface GroupHeaderTemplateContext {
  group: SelectGroupedOptions;
  first: boolean;
}

export type SelectGroupedSort = <T extends SelectGroupedOptions<any>>(a: T, b: T) => number;

const groupedOptionsDefaultSort: SelectGroupedSort = (a, b) =>
  a.title === b.title ? 0 : b.title ? -1 : 1;

// TODO blur event
@Component({
  selector: 'rt-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.disabled]': 'disabled',
    '[class.large-button]': 'largeButton',
  },
})
export class SelectComponent<Val extends any | any[]> extends RtInput<Val> {
  // Deprecated
  @Input()
  public set items(items: { option: string; value: Val }[]) {
    this.options = (items || []).map(item => ({ label: item.option, value: item.value }));
  }

  // Deprecated
  @Input()
  public set message(message: string) {
    this.noResultsMessage = message;
  }

  // Include empty option together with other options
  public get defaultIncludeEmptyOption(): boolean {
    return !this.multiple;
  }

  // Deprecated
  @Input()
  public set required(required: boolean) {
    this.includeEmptyOption = !required;
  }

  @Output()
  public get select(): any {
    // eslint-disable-next-line no-console
    (console.warn || console.log)(
      'SelectComponent.select is deprecated. Please use `valueChange` instead',
    );
    return this.valueChange;
  }

  public get hasGroups(): boolean {
    return this.groupedOptions && !!this.groupedOptions.length;
  }

  public get dropdownOpened(): boolean {
    return this.selectDropdownOverlay.open;
  }

  public get selectedItemUuid(): string | void {
    return (
      this.flatGroupsOptions &&
      this.flatGroupsOptions[this.selectedOptionIndex] &&
      this.flatGroupsOptions[this.selectedOptionIndex].uuid
    );
  }

  private get formattedOptions(): SelectOption[] {
    const options = [
      ...(this.includeEmptyOption ? [this.emptyOption] : []),
      ...(this.options || []),
    ];
    if (this.multiple) {
      return options.filter(option => !(<Val[]>this.value).find(v => option.value === v));
    } else {
      return options;
    }
  }
  @Input() public adjustDropdownWidthOnOpen: boolean = false;
  @Input() public options: SelectOption<any>[] = [];
  @Input() public groupedOptions: SelectGroupedOptions<any>[] = [];
  @Input() public groupedOptionsSort: SelectGroupedSort | false = groupedOptionsDefaultSort;
  @Input() public selectedOptionIconLeft?: IconName;

  @Input() public largeButton: boolean = false;
  @Input() public hasInputTrigger: boolean = false;

  // Prepends a label
  @Input() public label?: string;

  @Input() public tabIndex?: number;

  // Placeholder text
  public readonly defaultPlaceholder: string = this.i18n.translate('rtu.ui.select.placeholder');
  @WithDefaultValue('defaultPlaceholder')
  @Input()
  public placeholder?: string;

  // Enables search input
  public readonly defaultHasSearch: boolean = true;
  @WithDefaultValue('defaultHasSearch')
  @Input()
  public hasSearch?: boolean;

  public readonly defaultNotSelectable: boolean = false;
  @WithDefaultValue('defaultNotSelectable')
  @Input()
  public notSelectable?: boolean;

  // Search case sensitivity
  public readonly defaultSearchCaseSensitive: boolean = false;
  @WithDefaultValue('defaultSearchCaseSensitive')
  @Input()
  public searchCaseSensitive?: boolean;

  public readonly defaultHasStickyFooter: boolean = false;
  @WithDefaultValue('defaultHasStickyFooter')
  @Input()
  public hasStickyFooter?: boolean;

  public readonly defaultNoMaxHeight: boolean = false;
  @WithDefaultValue('defaultNoMaxHeight')
  @Input()
  public noMaxHeight?: boolean;

  // Limit shown options to first x options
  @Input() public limit?: number;

  // Message to display when there are no results
  public readonly defaultNoResultsMessage: string = this.i18n.translate('rtu.ui.select.no_results');
  @WithDefaultValue('defaultNoResultsMessage')
  @Input()
  public noResultsMessage?: string;
  @WithDefaultValue('defaultIncludeEmptyOption')
  @Input()
  public includeEmptyOption?: boolean;

  public readonly defaultEmptyOptionLabel: string = this.i18n.translate('rtu.ui.select.none');
  @WithDefaultValue('defaultEmptyOptionLabel')
  @Input()
  public emptyOptionLabel: string = this.defaultEmptyOptionLabel;

  // A selection dropdown can allow multiple selections.
  public readonly defaultMultiple: boolean = false;
  @WithDefaultValue('defaultMultiple')
  @Input()
  public multiple?: boolean;

  // Whether or not the menu should close when a value is selected from the dropdown.
  public readonly defaultCloseOnSelect: boolean = true;
  @WithDefaultValue('defaultCloseOnSelect')
  @Input()
  public closeOnSelect?: boolean;

  public readonly defaultHasDropdownSeparator: boolean = false;
  @WithDefaultValue('defaultHasDropdownSeparator')
  @Input()
  public hasDropdownSeparator?: boolean;

  // Whether or not dropdown icon should have margin
  public readonly defaultDropdownIconHasMargin: boolean = true;
  @WithDefaultValue('defaultDropdownIconHasMargin')
  @Input()
  public dropdownIconHasMargin?: boolean;

  // Custom element to trigger the menu to become visible. By default, shows select/input element (depending on the variant).
  @Input() public triggerTemplate?: TemplateRef<any>;
  @Input() public triggerTemplateContext?: Record<string, any> = {};

  @Input() public footerTemplate?: TemplateRef<any>;
  @Input() public footerTemplateContext?: Record<string, any> = {};

  // Option dropdown element
  @Input() public optionTemplate?: TemplateRef<SelectOptionContext<any>>;

  // Selected option label element (used only when `multiple`)
  @Input() public labelTemplate?: TemplateRef<any>;

  @Input() public groupHeaderTemplate?: TemplateRef<any>;
  @Input() public virtualScrollEnabled: boolean = true;

  @Input() public hasDivider: boolean;

  @Input()
  public searchPlaceholder: string;

  // Whether or not wrap text in default label
  public readonly defaultWrapLabelText: boolean = false;

  @WithDefaultValue('defaultWrapLabelText')
  @Input()
  public wrapLabelText?: boolean;
  public SelectOptionContext: SelectOptionContext<SelectOptionValue>;
  public TriggerTemplateContext: TriggerTemplateContext;
  public LabelTemplateContext: LabelTemplateContext;
  public GroupHeaderTemplateContext: GroupHeaderTemplateContext;
  public GroupsTemplateContext: GroupsTemplateContext;
  public ListTemplateContext: SelectListTemplateContext;

  public GroupsOrListTemplateContext: GroupsTemplateContext | SelectListTemplateContext;
  public searchQuery: string | null = null;

  public dropdownWidth?: number;
  public virtualScrollThresholdExceeded: boolean = false;

  // Used when multiple === false
  public selectedOption: SelectOption | null = null;
  // Used when multiple === true
  public selectedOptions: SelectOption[] = [];

  public selectedOptionIndex: number = -1;

  public filteredOptions: SelectOption[] = [];
  public filteredGroupsOptions: SelectGroupedOptions[] = [];

  public flatGroupsOptions: SelectGroupOption[] = [];

  public scrollTop: number = 0;

  // TODO move it to variant
  @Input() public light: boolean = false;
  @Input() public dark: boolean = false;
  @Input() public radiusDefault: boolean = false;

  // If not defined, the width is set automatically to the container's width
  @Input() public minDropdownWidth?: number | 'max-content';
  @Input() public maxDropdownWidth?: number;

  // If not set, the position is set automatically to the trigger's position
  @Input() public dropdownPositions?: ConnectionPositionPair[];

  @Input() public openDropdownOnInit: boolean = false;

  @Input() public hasItemPadding: boolean = false;

  @Input() public labelOverride: string | null = null;

  @Input() public boldPlaceholder?: boolean = false;

  @Input() public itemTextLeftPadding?: boolean = false;

  @Input() public itemTextNoPadding?: boolean = false;

  @Input() public iconEmptyLabel?: boolean = false;

  @Input() public optionWordBreak: boolean = false;
  @Input() public hasWhiteBackground: boolean = false;

  @Input() public hideSelectedOption: boolean = false;
  @Input() public isInvalid: boolean = false;

  @ViewChild('selectDefaultTemplate') private selectDefaultTemplate: TemplateRef<any>;
  @ViewChild('optionDefaultTemplate', { static: true })
  private optionDefaultTemplate: TemplateRef<any>;
  @ViewChild('labelDefaultTemplate', { static: true })
  private labelDefaultTemplate: TemplateRef<any>;
  @ViewChild('footerDefaultTemplate', { static: true })
  private footerDefaultTemplate: TemplateRef<any>;
  @ViewChild('selectDropdownOverlay', { static: true })
  private selectDropdownOverlay: ConnectedOverlayDirective;
  @ViewChild('dropdownContainer', { static: true }) private dropdownContainer: any;
  @ViewChild('trigger') private trigger: any;
  @ViewChild('searchQueryInput') private searchQueryInput: any;
  @ViewChildren('filteredOptions') private filteredOptionNodes: any;

  private readonly virtualScrollThreshold = 20;
  private emptyOption: SelectOption = { label: this.emptyOptionLabel, value: '' };
  private emptyGroupedOption: SelectGroupedOptions = {
    title: null,
    options: [this.emptyOption],
  };

  private subscriptions: { unsubscribe: () => void }[] = [];
  private destroyed: boolean = false;

  constructor(
    elementRef: ElementRef,
    renderer2: Renderer2,
    @Optional()
    @Self()
    ngControl: NgControl,
    cdRef: ChangeDetectorRef,
    private i18n: I18nService,
    private windowRef: WindowRef,
  ) {
    super(elementRef, renderer2, ngControl, cdRef);
  }

  public onVirtualScrollUpdate(visibleItems: SelectOption[] | SelectGroupedOptions[]): void {
    this.cdRef.detectChanges();
  }

  public ngOnInit(): void {
    this.subscriptions.push(
      this.selectDropdownOverlay.attach.subscribe(() => {
        this.updateDropdownPostionAsync();
        this.setTimeout(() => {
          this.cdRef.detectChanges();
        });
      }),
    );
    this.subscriptions.push(
      this.selectDropdownOverlay.detach.subscribe(() => {
        this.setTimeout(() => {
          this.cdRef.detectChanges();
        });
      }),
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.subscriptions.length = 0;
    this.destroyed = true;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes.options && changes.options.previousValue !== changes.options.currentValue) ||
      (changes.groupedOptions &&
        changes.groupedOptions.previousValue !== changes.groupedOptions.currentValue) ||
      (changes.items && changes.items.previousValue !== changes.items.currentValue) ||
      (changes.value && changes.value.previousValue !== changes.value.currentValue)
    ) {
      this.calculateOptions();
      this.calculateSelectedOption();
      this.calculateSelectedOptionIndex();
      this.updateDropdownPostionAsync();
    }
    if (changes.emptyOptionLabel) {
      this.emptyOption = { label: this.emptyOptionLabel!, value: '' };
    }
  }

  // eslint-disable-next-line max-lines
  public ngAfterViewInit(): void {
    if (!this.minDropdownWidth) {
      this.adjustDropdownWidth();
    }
    this.setTimeout(() => {
      this.adjustDropdownWidth();
      this.cdRef.detectChanges();
    }, 0);

    this.subscriptions.push(
      observableFromEvent(this.windowRef.nativeWindow, 'resize')
        .pipe(debounceTime(50))
        .subscribe(() => {
          this.adjustDropdownWidth();
        }),
    );

    if (this.openDropdownOnInit) {
      this.toggleDropdown(true);
    }
  }

  public setFocus(): void {
    if (this.trigger && this.trigger.setFocus) {
      this.trigger.setFocus();
    }
  }

  public setSearchInputFocus(): void {
    if (this.searchQueryInput) {
      this.searchQueryInput.setFocus();
    }
  }

  public selectOption(option: SelectOption): void {
    if (this.notSelectable) {
      return;
    }

    const nextValue = this.multiple
      ? (<SelectOptionValue[] | any>this.value || [])
          .filter((v: any) => v !== option.value)
          .concat([option.value])
      : option && option.value;
    this.handleChange(nextValue);
    // @ts-ignore
    if (!this.cdRef['destroyed']) {
      if (this.closeOnSelect && this.dropdownOpened) {
        this.toggleDropdown(false);
      }
      this.cdRef.detectChanges();
    }

    if (!this.minDropdownWidth) {
      this.setTimeout(() => {
        this.adjustDropdownWidth();
        // @ts-ignore
        if (!this.cdRef['destroyed']) {
          this.cdRef.detectChanges();
        }
      }, 0);
    }
  }

  public changeSearchQuery(searchQuery: string | null): void {
    this.searchQuery = ['', null, undefined].indexOf(searchQuery) !== -1 ? null : searchQuery;
    this.calculateOptions();
    this.selectedOptionIndex = this.searchQuery !== null ? 0 : -1;
    this.updateDropdownPostionAsync();
    this.cdRef.detectChanges();
  }

  // TODO fix focus issues:
  // - TAB and SHIFT-TAB should always work
  //   (whether we tab to the input, click on it; whether the dropdown menu is still open or not)
  // - if we are focused on input without the dropdown menu opened;
  //   UP and DOWN shouldnt defocus the select
  // eslint-disable-next-line max-lines-per-function
  public handleKeyDown(event: KeyboardEvent): void {
    if (this.disabled || this.readOnly) {
      return;
    }
    const key = event.which || event.keyCode;
    switch (key) {
      case KeyCode.Escape:
        if (this.dropdownOpened) {
          event.preventDefault();
          this.toggleDropdown(false);
          this.setFocus();
        }
        break;

      case KeyCode.Tab:
        if (this.dropdownOpened) {
          this.toggleDropdown(false);
        }
        this.setFocus();
        break;

      case KeyCode.Enter:
      case KeyCode.Space:
        event.preventDefault();
        if (this.dropdownOpened) {
          const selectedOption =
            this.selectedOptionIndex !== -1
              ? this.filteredOptions[this.selectedOptionIndex] ||
                this.flatGroupsOptions[this.selectedOptionIndex]
              : undefined;

          if (selectedOption) {
            this.selectOption(selectedOption);
          }
        } else {
          this.toggleDropdown(true);
        }
        break;

      case KeyCode.UpArrow:
        event.preventDefault();
        if (this.dropdownOpened) {
          this.selectUp();
          // this.setFocus();
          this.scrollToCurrentOption();
        } else {
          this.toggleDropdown(true);
        }
        break;

      case KeyCode.DownArrow:
        event.preventDefault();
        if (this.dropdownOpened) {
          this.selectDown();
          // this.setFocus();
          this.scrollToCurrentOption();
        } else {
          this.toggleDropdown(true);
        }
        break;

      default:
        return;
    }

    this.cdRef.detectChanges();
  }

  // eslint-disable-next-line max-lines-per-function
  public handleSearchInputKeydown(event: KeyboardEvent): void {
    if (this.disabled || this.readOnly) {
      return;
    }

    const key = event.which || event.keyCode;
    switch (key) {
      case KeyCode.Escape:
        event.preventDefault();
        this.setFocus();
        this.toggleDropdown(false);
        this.setTimeout(() => this.setFocus());
        break;

      case KeyCode.Tab:
        this.setFocus();
        this.setTimeout(() => {
          this.toggleDropdown(false);
        });
        break;

      case KeyCode.Enter:
        event.preventDefault();
        this.setFocus();
        if (this.selectedOptionIndex !== -1) {
          this.selectOption(
            this.filteredOptions[this.selectedOptionIndex] ||
              this.flatGroupsOptions[this.selectedOptionIndex],
          );
        }
        this.setTimeout(() => this.setFocus());
        break;

      case KeyCode.UpArrow:
        event.preventDefault();
        this.selectUp();
        this.setSearchInputFocus();
        this.scrollToCurrentOption();
        break;

      case KeyCode.DownArrow:
        event.preventDefault();
        this.selectDown();
        this.setSearchInputFocus();
        this.scrollToCurrentOption();
        break;

      default:
        return;
    }

    this.cdRef.detectChanges();
  }

  public removeValue = (optionValue: Val) => {
    const nextValue = (<any[]>(this.value || [])).filter(v => v !== optionValue);
    this.handleChange(<Val>nextValue);
    this.cdRef.detectChanges();
  };

  public toggleDropdown = (open: boolean = !this.dropdownOpened) => {
    if (this.adjustDropdownWidthOnOpen) {
      this.adjustDropdownWidth();
    }

    this.selectDropdownOverlay.open = open;
    if (open) {
      this.changeSearchQuery(null);
    }
    this.cdRef.detectChanges();
  };

  public trackByOption<T extends SelectOption>(index: number, option: T): number | string | null {
    if (option) {
      return option.value;
    } else {
      return index;
    }
  }

  public trackByGroup(index: number, group: SelectGroupedOptions): string {
    return `${index}__${JSON.stringify(group.options)}`;
  }

  public scrollToCurrentOption(): void {
    if (
      this.filteredOptionNodes._results &&
      this.filteredOptionNodes._results[this.selectedOptionIndex] &&
      this.filteredOptionNodes._results[this.selectedOptionIndex].nativeElement
    ) {
      this.scrollTop =
        this.filteredOptionNodes._results[this.selectedOptionIndex].nativeElement.offsetTop;
    }
  }

  protected setValue(value: Val): void {
    this.value = value;
    this.calculateSelectedOption();
    this.calculateSelectedOptionIndex();
    this.updateDropdownPostionAsync();
  }

  private calculateSelectedOptionIndex(): void {
    this.selectedOptionIndex = (this.filteredOptions || this.flatGroupsOptions || []).findIndex(
      option => option.value === this.value,
    );
  }

  private calculateSelectedOption(): void {
    if (this.multiple) {
      // @ts-ignore
      this.selectedOptions = (<SelectOptionValue[]>this.value || [])
        .map(v => this.options.find(option => option.value === v))
        .filter(o => !!o)!;
    } else {
      this.selectedOption =
        (this.hasGroups ? this.flatGroupsOptions : this.options || []).find(
          option => option.value === this.value,
        ) || this.emptyOption;
    }
  }

  private calculateOptions(): void {
    this.hasGroups ? this.setGroupsOptions() : this.setDefaultOptions();
  }

  private setGroupsOptions(): void {
    const opts = this.includeEmptyOption
      ? [this.emptyGroupedOption, ...this.groupedOptions]
      : this.groupedOptions;

    const filteredGroupsOptions = opts
      .map(({ title, options }) => {
        const filteredOptions =
          this.searchQuery !== null
            ? options
                .filter(item => this.matchSearchQuery(item))
                .map(option => ({ ...option, uuid: generateUuid() }))
            : options.map(option => ({ ...option, uuid: generateUuid() }));

        return {
          title,
          options: filteredOptions,
        };
      })
      .filter(group => group.options.length);

    if (this.groupedOptionsSort) {
      filteredGroupsOptions.sort(this.groupedOptionsSort);
    }

    this.flatGroupsOptions = (filteredGroupsOptions || this.groupedOptions)
      .map(group => group.options)
      .reduce((a, b) => a.concat(b), []);

    this.filteredGroupsOptions = filteredGroupsOptions;
    this.toggleVirtualScroll();
  }

  private toggleVirtualScroll() {
    const items = (this.hasGroups ? this.filteredGroupsOptions : this.filteredOptions) || [];
    this.virtualScrollThresholdExceeded = items.length > this.virtualScrollThreshold;
  }

  private setDefaultOptions(): void {
    const filteredOptions =
      this.searchQuery !== null
        ? this.formattedOptions.filter(item => this.matchSearchQuery(item))
        : this.formattedOptions;

    this.filteredOptions = this.limit ? filteredOptions.slice(0, this.limit) : filteredOptions;
    this.toggleVirtualScroll();
  }

  private matchSearchQuery(item: SelectOption): boolean {
    if (this.searchCaseSensitive) {
      return (
        item.label.includes(this.searchQuery!) ||
        this.concatAdditionalSearchKeys(item).includes(this.searchQuery!)
      );
    } else {
      return (
        includesWithTrim(item.label, this.searchQuery!, { trim: false }) ||
        includesWithTrim(this.concatAdditionalSearchKeys(item), this.searchQuery!, { trim: false })
      );
    }
  }

  private concatAdditionalSearchKeys(item: SelectOption): string {
    if (item && item.additionalSearchKeys && item.additionalSearchKeys.length) {
      return item.additionalSearchKeys.reduce((agg, key) => `${agg};${key}`);
    } else {
      return '';
    }
  }

  private adjustDropdownWidth(): void {
    this.dropdownWidth = this.dropdownContainer.nativeElement.offsetWidth;
  }

  private selectUp(): void {
    const startIndex = this.selectedOptionIndex;
    const collection = (this.hasGroups ? this.flatGroupsOptions : this.filteredOptions) || [];

    do {
      if (this.selectedOptionIndex === 0 || this.selectedOptionIndex > collection.length - 1) {
        this.selectedOptionIndex = collection.length - 1;
      } else {
        this.selectedOptionIndex--;
      }
    } while (
      this.selectedOptionIndex !== startIndex &&
      collection[this.selectedOptionIndex] &&
      collection[this.selectedOptionIndex].disabled
    );
  }

  private selectDown(): void {
    const startIndex = this.selectedOptionIndex;
    const collection = (this.hasGroups ? this.flatGroupsOptions : this.filteredOptions) || [];
    do {
      if (this.selectedOptionIndex >= collection.length - 1) {
        this.selectedOptionIndex = 0;
      } else {
        this.selectedOptionIndex++;
      }
    } while (
      this.selectedOptionIndex !== startIndex &&
      collection[this.selectedOptionIndex] &&
      collection[this.selectedOptionIndex].disabled
    );
  }

  private updateDropdownPostionAsync(): void {
    this.setTimeout(() => {
      if (this.selectDropdownOverlay.overlayRef) {
        this.selectDropdownOverlay.overlayRef.updatePosition();
      }
    });
  }

  private setTimeout(fn: () => any, time: number = 0): any {
    return setTimeout(() => {
      if (!this.destroyed) {
        fn();
      }
    }, time);
  }
}
